<script setup lang="ts">
/**
 * Customer-unit split from buffet item_facts.
 *
 * The backend deliberately omits this block when the POS export has no
 * item_facts coverage. An absent split is therefore an unavailable signal,
 * never a zero-valued card.
 */
import { computed, ref } from 'vue';
import type { CustSplit } from '@/api';
import { deltaClass as deltaClassFor, deltaSign, fmtDelta } from '@/lib/weekly';

const props = withDefaults(defineProps<{
  split: CustSplit | null | undefined;
  /** Initial disclosure state; home keeps this secondary signal collapsed. */
  collapsed?: boolean;
}>(), {
  collapsed: false,
});

const expanded = ref(!props.collapsed);
const detailsId = 'cust-split-details';

const split = computed(() => props.split);

const totalUnits = computed(() => {
  if (!split.value) return 0;
  return split.value.adult_units + split.value.kid_units;
});

const adultSharePct = computed(() => {
  if (totalUnits.value === 0 || !split.value) return 0;
  return (split.value.adult_units / totalUnits.value) * 100;
});

const kidSharePct = computed(() => {
  if (totalUnits.value === 0 || !split.value) return 0;
  return (split.value.kid_units / totalUnits.value) * 100;
});

const adultDelta = computed(() => {
  const value = split.value?.adult_delta_pct;
  return split.value?.comparison_available && value != null ? value : null;
});

const kidDelta = computed(() => {
  const value = split.value?.kid_delta_pct;
  return split.value?.comparison_available && value != null ? value : null;
});

function formatUnits(value: number): string {
  return value.toLocaleString('th-TH');
}

function deltaClass(value: number): string {
  return deltaClassFor(value, 'review', 'cust-split__delta');
}

function deltaLabel(value: number): string {
  return `(${deltaSign(value)} ${fmtDelta(value)}% เทียบสัปดาห์ก่อน)`;
}
</script>

<template>
  <!-- No item_facts coverage is an unavailable signal, not a zero. -->
  <section v-if="split" class="cust-split" aria-label="สัดส่วนผู้ใหญ่/เด็ก">
    <header class="cust-split__header">
      <h2 class="cust-split__title">สัดส่วนผู้ใหญ่/เด็ก*</h2>
      <button
        type="button"
        class="cust-split__toggle"
        :aria-expanded="expanded"
        :aria-controls="expanded ? detailsId : undefined"
        :aria-label="expanded ? 'ซ่อนรายละเอียดสัดส่วนผู้ใหญ่และเด็ก' : 'เปิดดูรายละเอียดสัดส่วนผู้ใหญ่และเด็ก'"
        @click="expanded = !expanded"
      >
        <span>{{ expanded ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด' }}</span>
        <span class="cust-split__chevron" aria-hidden="true">{{ expanded ? '⌃' : '⌄' }}</span>
      </button>
    </header>

    <div v-if="expanded" :id="detailsId" class="cust-split__details">
      <div class="cust-split__tiles">
        <article class="cust-split__tile cust-split__tile--adult">
          <span class="cust-split__tile-label">ผู้ใหญ่*</span>
          <div class="cust-split__value-row">
            <strong class="cust-split__value">{{ formatUnits(split.adult_units) }} <small>หน่วย</small></strong>
            <span v-if="adultDelta != null" class="cust-split__delta" :class="deltaClass(adultDelta)">
              {{ deltaLabel(adultDelta) }}
            </span>
          </div>
        </article>

        <article class="cust-split__tile cust-split__tile--kid">
          <span class="cust-split__tile-label">เด็ก*</span>
          <div class="cust-split__value-row">
            <strong class="cust-split__value">{{ formatUnits(split.kid_units) }} <small>หน่วย</small></strong>
            <span v-if="kidDelta != null" class="cust-split__delta" :class="deltaClass(kidDelta)">
              {{ deltaLabel(kidDelta) }}
            </span>
          </div>
        </article>
      </div>

      <div
        v-if="totalUnits !== 0"
        class="cust-split__share"
        role="img"
        :aria-label="`สัดส่วนผู้ใหญ่ ${adultSharePct.toFixed(1)}% เด็ก ${kidSharePct.toFixed(1)}%`"
      >
        <div class="cust-split__share-track" aria-hidden="true">
          <span class="cust-split__share-adult" :style="{ width: `${adultSharePct}%` }"></span>
          <span class="cust-split__share-kid" :style="{ width: `${kidSharePct}%` }"></span>
        </div>
        <div class="cust-split__legend" aria-hidden="true">
          <span><i class="cust-split__legend-dot cust-split__legend-dot--adult"></i>ผู้ใหญ่</span>
          <span><i class="cust-split__legend-dot cust-split__legend-dot--kid"></i>เด็ก</span>
        </div>
      </div>

      <p v-if="split.comparison_available && split.matched_days != null" class="cust-split__matched-days">
        เทียบวันที่ตรงกัน {{ split.matched_days }} วัน
      </p>
      <p class="cust-split__footnote">*หน่วยจากรายการบุฟเฟต์ใน POS — ค่าประมาณ ไม่ใช่จำนวนหัว</p>
    </div>
  </section>
</template>

<style scoped>
.cust-split {
  margin: 0 0 1rem;
  padding: 0.85rem;
  background: var(--surface-card, var(--surface));
  border: 1px solid var(--border-card, var(--line));
  border-top: 3px solid var(--sales);
  border-radius: var(--radius-card);
  color: var(--text-main, var(--text));
}

.cust-split__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid var(--line, var(--border-card));
}

.cust-split__title {
  margin: 0;
  color: var(--text-main, var(--text));
  font: 700 0.95rem/1.35 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
}

.cust-split__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  min-height: var(--tap-min, 44px);
  padding: 0.2rem 0;
  border: 0;
  background: transparent;
  color: var(--text-body, var(--text-dim));
  font: 600 0.7rem/1.3 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  white-space: nowrap;
}

.cust-split__toggle:focus-visible {
  outline: 2px solid var(--color-brand-gold, var(--sales));
  outline-offset: 2px;
  border-radius: var(--radius-badge);
}

.cust-split__chevron {
  color: var(--sales);
  font-size: 1rem;
  line-height: 1;
}

.cust-split__details {
  display: grid;
  gap: 0.65rem;
  margin-top: 0.7rem;
}

.cust-split__tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}

.cust-split__tile {
  min-width: 0;
  padding: 0.65rem 0.7rem;
  background: var(--surface-inset);
  border: 1px solid var(--border-card, var(--line));
  border-radius: 8px;
}

.cust-split__tile-label {
  display: block;
  color: var(--text-body, var(--text-dim));
  font: 600 0.72rem/1.3 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
}

.cust-split__value-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.25rem 0.45rem;
  margin-top: 0.15rem;
}

.cust-split__value {
  color: var(--text-main, var(--text));
  font: 700 1.15rem/1.25 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cust-split__value small {
  color: var(--text-body, var(--text-dim));
  font-size: 0.72rem;
  font-weight: 600;
}

.cust-split__delta {
  color: var(--text-body, var(--text-dim));
  font: 600 0.66rem/1.35 var(--font-display, 'Kanit', 'Sarabun', sans-serif);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cust-split__delta--good { color: var(--color-good, var(--ok)); }
.cust-split__delta--bad { color: var(--color-bad, var(--degraded)); }

.cust-split__share {
  display: grid;
  gap: 0.3rem;
}

.cust-split__share-track {
  display: flex;
  width: 100%;
  height: 8px;
  overflow: hidden;
  background: var(--surface-track, var(--surface-inset));
  border-radius: var(--radius-bar, 5px);
}

.cust-split__share-adult { background: var(--sales); }
.cust-split__share-kid { background: var(--sales-light); }

.cust-split__legend {
  display: flex;
  gap: 0.8rem;
  color: var(--text-muted);
  font: 500 0.64rem/1.3 var(--font-body, 'Sarabun', sans-serif);
}

.cust-split__legend > span {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.cust-split__legend-dot {
  width: 7px;
  height: 7px;
  border-radius: var(--radius-round, 50%);
}

.cust-split__legend-dot--adult { background: var(--sales); }
.cust-split__legend-dot--kid { background: var(--sales-light); }

.cust-split__matched-days,
.cust-split__footnote {
  margin: 0;
  font: 500 0.66rem/1.4 var(--font-body, 'Sarabun', sans-serif);
}

.cust-split__matched-days { color: var(--text-muted); }

.cust-split__footnote {
  color: var(--color-watch, #B77400);
}

@media (max-width: 380px) {
  .cust-split__tiles { grid-template-columns: 1fr; }
}
</style>
